import { ApplicationType, BuildingProductType, Permission } from '../enums/enums';
import { Injectable } from '@angular/core';
import * as _ from 'underscore';
import { Router } from '@angular/router';
import { TokenService } from './token.service';
import { BusinessRulesService } from '../../../services/business-rules.service';
import { camelCase } from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {

  private permissions$: Observable<Permission[]>;
  private permissionsByProduct$: Observable<Permission[]>;
  // private products$: Observable<BuildingProductType[]>;

  constructor(
    private tokenService: TokenService,
    private router: Router,
    private businessRulesService: BusinessRulesService) {

    const tokenPermissions = this.tokenService.getTokenPermissions();
    let loginPermissions: Permission[] = [];

    //filter out permissions that aren't allowed for the current subscription
    this.permissionsByProduct$ = this.businessRulesService.getPermissionsByProduct().pipe(map((permissionsByProduct) => {
      const businessProducts = this.tokenService.getBusinessProducts();

      // tslint:disable-next-line: forin
      for (const i in businessProducts) {
        const product = businessProducts[i];
        const productPermissions = permissionsByProduct[camelCase(product)];
        if (productPermissions) { // only add the subscription permissions that are also in the token
          loginPermissions = loginPermissions.concat(productPermissions.filter((p: Permission) => tokenPermissions.includes(p)));
        }
      }

      loginPermissions = this.determineExecutorFeaturePermissions(loginPermissions, businessProducts);

      if(loginPermissions.length === 0){
        loginPermissions.push(Permission.PAYMENT);
      }

      return loginPermissions;
    }));

    this.permissions$ = combineLatest([this.businessRulesService.getDefaultPermissions(), this.permissionsByProduct$]).pipe(
      map(([defaultPermissions, productPermissions]) => [...defaultPermissions, ...productPermissions]),
    );

    // this.products$ = Observable.create(this.tokenService.getBusinessProducts());
  }

  public isPermitted(componentPermissions: Permission[]): Observable<boolean> {
    return this.getPermissions().pipe(map((permissions) => {
      if (permissions.length > 0) {
        const intersectionCount = _.intersection(componentPermissions, permissions).length;
        return componentPermissions.length === 0 || intersectionCount === componentPermissions.length;
      }
      return false;
    }));
  }

  public navigateToForbbidenPage() {
    this.router.navigateByUrl('/forbidden-page', { skipLocationChange: true });
  }

  public getPermissions(): Observable<Permission[]> {
    return this.permissions$;
  }

  public getpermissionsByProduct() {
    return this.permissionsByProduct$;
  }

  public doesUserHavePermission(permission: Permission): Observable<boolean> {
    return this.getPermissions().pipe(map((response) => {
      return response.includes(permission);
    }));
  }

  // public hasUnitInspectionsProduct(): Observable<boolean> {
  //   return this.products$.pipe(map((response) => response.includes(BuildingProductType.UNITINSPECTIONS)));
  // }

  public hasUnitInspectionsProduct(): boolean {
    return this.tokenService.getBusinessProducts().includes(BuildingProductType.UNITINSPECTIONS);
  }

  public doesUserHavePermissionToAddBuilding(): Observable<boolean> {
    return this.doesUserHavePermission(Permission.BUILDINGSADD);
  }

  public doesUserHavePermissionToEditBuilding(): Observable<boolean> {
    return this.getPermissions().pipe(map((response) => {
      return response.includes(Permission.BUILDINGSEDIT);
    }));
  }

  public doesUserHavePermissionToViewRoles(): Observable<boolean> {
    return this.getPermissions().pipe(map((response) => {
      return response.includes(Permission.ROLESEDIT);
    }));
  }

  public doesUserHavePermissionToViewContacts(): Observable<boolean> {
    return this.getPermissions().pipe(map((response) => {
      return response.includes(Permission.CONTACTSVIEW);
    }));
  }

  public doesUserHavePermissionToEditContacts(): Observable<boolean> {
    return this.getPermissions().pipe(map((response) => {
      return response.includes(Permission.CONTACTSEDIT);
    }));
  }

  public doesUserHavePermissionToViewAccounts(): Observable<boolean> {
    return this.getPermissions().pipe(map((response) => {
      return response.includes(Permission.USERMANAGEMENT);
    }));
  }

  public doesUserHavePermissionToViewPayment(): Observable<boolean> {
    return this.permissionsByProduct$.pipe(map((response) => {
      return response.includes(Permission.PAYMENT);
    }));
  }

  public doesUserHavePermissionToViewDevices(): Observable<boolean> {

    return this.doesUserHavePermission(Permission.DEVICESVIEW);
  }

  public doesUserHavePermissionToViewPartners(): Observable<boolean> {
    // BLDNG-859: this is not yet relevant, there for we hide it
    return of(false);//this.doesUserHavePermission(Permission.PARTNERMANAGEMENT);
  }

  public doesUserHavePermissionToStartIntake(): Observable<boolean> {
    return this.getPermissions().pipe(map((response) => {
      return response.includes(Permission.INSPECTIONSEXECUTEINTAKE);
    }));
  }

  /**
   * Adds the Executor feature for customer that have
   * a Pro or Premium subscription but don't have
   * a Maintenance product.
   * It is added ad-hoc here in the client due
   * to the excessive refactor required to
   * add them in the backend.
   * @param loginPermissions
   * @param businessProducts
   * @private
   */
  private determineExecutorFeaturePermissions(loginPermissions: Permission[], businessProducts: BuildingProductType[]): Permission[] {
    const applications = this.tokenService.getApplications();
    const hasMaintenance = applications.includes(ApplicationType.MAINTENANCE);
    const hasBuildingProOrPremium = businessProducts.includes(BuildingProductType.PREMIUMSUBSCRIPTION)
      || businessProducts.includes((BuildingProductType.PROSUBSCRIPTION));
    const executorPermissions = !hasMaintenance && hasBuildingProOrPremium ? [Permission.MANAGERBUILDINGEXECUTORSVIEW] : [];
    return [...loginPermissions, ...executorPermissions];
  }
}

